/**
 * Settings Page
 * Manages iTop API credentials
 */

import './taskpane.css';
import { CredentialService } from '../services/credential.service';
import { ITopApiService } from '../services/itop-api.service';
import {
  getElementById,
  showMessage,
  hideMessage,
  setButtonLoading,
  waitForOffice
} from '../utils/helpers';

// DOM Elements
let usernameInput: HTMLInputElement;
let passwordInput: HTMLInputElement;
let urlInput: HTMLInputElement;
let messageDiv: HTMLElement;
let testBtn: HTMLButtonElement;
let saveBtn: HTMLButtonElement;
let clearBtn: HTMLButtonElement;

/**
 * Initialize the page
 */
async function initialize(): Promise<void> {
  await waitForOffice();

  // Get DOM elements
  usernameInput = getElementById<HTMLInputElement>('username')!;
  passwordInput = getElementById<HTMLInputElement>('password')!;
  urlInput = getElementById<HTMLInputElement>('url')!;
  messageDiv = getElementById<HTMLElement>('message')!;
  testBtn = getElementById<HTMLButtonElement>('testBtn')!;
  saveBtn = getElementById<HTMLButtonElement>('saveBtn')!;
  clearBtn = getElementById<HTMLButtonElement>('clearBtn')!;

  // Set default URL
  urlInput.placeholder = CredentialService.DEFAULT_URL;

  // Load existing credentials if any
  loadExistingCredentials();

  // Set up event listeners
  testBtn.addEventListener('click', handleTestConnection);
  saveBtn.addEventListener('click', handleSave);
  clearBtn.addEventListener('click', handleClear);
}

/**
 * Load existing credentials into form
 */
function loadExistingCredentials(): void {
  const credentials = CredentialService.get();
  if (credentials) {
    usernameInput.value = credentials.username;
    // Don't populate password for security
    urlInput.value = credentials.url;
    showMessage(messageDiv, 'Credentials are configured. Enter password to update.', 'info');
  }
}

/**
 * Handle test connection button
 */
async function handleTestConnection(): Promise<void> {
  const username = usernameInput.value.trim();
  const password = passwordInput.value;
  const url = urlInput.value.trim() || CredentialService.DEFAULT_URL;

  if (!username || !password) {
    showMessage(messageDiv, 'Please enter username and password.', 'error');
    return;
  }

  setButtonLoading(testBtn, true, 'Testing...');
  hideMessage(messageDiv);

  try {
    const success = await ITopApiService.testConnection(username, password, url);

    if (success) {
      showMessage(messageDiv, 'Connection successful! You can now save your credentials.', 'success');
    } else {
      showMessage(messageDiv, 'Connection failed. Please check your credentials.', 'error');
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    showMessage(messageDiv, `Connection error: ${errorMessage}`, 'error');
  } finally {
    setButtonLoading(testBtn, false);
  }
}

/**
 * Handle save button
 */
async function handleSave(): Promise<void> {
  const username = usernameInput.value.trim();
  const password = passwordInput.value;
  const url = urlInput.value.trim() || CredentialService.DEFAULT_URL;

  if (!username || !password) {
    showMessage(messageDiv, 'Please enter username and password.', 'error');
    return;
  }

  setButtonLoading(saveBtn, true, 'Saving...');
  hideMessage(messageDiv);

  try {
    // Test connection before saving
    const success = await ITopApiService.testConnection(username, password, url);

    if (!success) {
      showMessage(messageDiv, 'Cannot save: Connection test failed. Please check your credentials.', 'error');
      return;
    }

    // Save credentials
    await CredentialService.save(username, password, url);

    // Clear the API cache since credentials changed
    ITopApiService.clearCache();

    showMessage(messageDiv, 'Credentials saved successfully!', 'success');

    // Clear password field for security
    passwordInput.value = '';
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    showMessage(messageDiv, `Failed to save credentials: ${errorMessage}`, 'error');
  } finally {
    setButtonLoading(saveBtn, false);
  }
}

/**
 * Handle clear button
 */
async function handleClear(): Promise<void> {
  if (!confirm('Are you sure you want to clear your saved credentials?')) {
    return;
  }

  setButtonLoading(clearBtn, true, 'Clearing...');
  hideMessage(messageDiv);

  try {
    await CredentialService.clear();
    ITopApiService.clearCache();

    // Clear form
    usernameInput.value = '';
    passwordInput.value = '';
    urlInput.value = '';

    showMessage(messageDiv, 'Credentials cleared.', 'info');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    showMessage(messageDiv, `Failed to clear credentials: ${errorMessage}`, 'error');
  } finally {
    setButtonLoading(clearBtn, false);
  }
}

// Initialize when Office is ready
Office.onReady(() => {
  initialize();
});
